import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFaqsForPublic } from "../../services/Faq";
import { Helmet } from "react-helmet-async";


const features = [
  {
    title: "Member Directory",
    description:
      "Manage members, families, contact details and volunteer information effortlessly.",
    icon: "diversity_4",
    bg: "bg-[#FFDCDB]",
    color: "text-[#FF7873]",
  },
  {
    title: "Ministries & Groups",
    description:
      "Organize ministries, assign leaders, and keep every team working together.",
    icon: "folded_hands",
    bg: "bg-[#FFE0FE]",
    color: "text-[#FF3483]",
  },
  {
    title: "Events & Schedules",
    description:
      "Create church events, schedule services, manage registrations, and send reminders.",
    icon: "event",
    bg: "bg-[#BED6FF]",
    color: "text-[#3A82FF]",
  },
  {
    title: "Leadership",
    description:
      "Manage pastors, elders, ministry leaders, and staff with organized roles and responsibilities.",
    icon: "person",
    bg: "bg-[#FFCBFE]",
    color: "text-[#91008D]",
  },
  {
    title: "Member Portal",
    description:
      "Give members secure access to their profiles, events, groups, giving, and church updates.",
    icon: "open_in_new",
    bg: "bg-[#D8FFE8]",
    color: "text-[#1DB25B]",
  },
  {
    title: "Push Notifications",
    description:
      "Send alerts, event reminders, and important messages directly to members' devices.",
    icon: "campaign",
    bg: "bg-[#B6FFF8]",
    color: "text-[#1DA193]",
  },
];

export const Home = () => {
  const navigate = useNavigate();
  const [faqData, setFaqData] = useState<any[]>([]);
  const [active, setActive] = useState<null | number>(null);

  useEffect(() => {
    getFaqsForPublic()
      .then(data => setFaqData(data))
      .catch(error => console.error('Error fetching FAQ data:', error));
  }, []);

  return (
    <>
      <Helmet>
        <title>
          My Church Directory | Church Management Software for Modern Churches
        </title>

        <meta
          name="description"
          content="Simplify church management with My Church Directory. Manage members, ministries, events, leadership, communication, and mobile engagement from one secure church management platform."
        />
      </Helmet>
      <section className="relative h-[90vh] overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/video/header_video.mp4" type="video/mp4" />
        </video>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-white/15 backdrop-blur-[4px] z-0" />

        {/* Content */}
        <div className="relative z-10 flex h-full items-center justify-center">
          <div className="max-w-3xl text-center px-6">

            <span className="different_heading lg:text-[68px] text-[48px] font-semibold text-black leading-[1]">
              Church Management<br />
              <span className="different_heading italic text-secondary">
                Made Simple
              </span>
            </span>

            <p className="mt-8 text-lg text-[#191c21] leading-8 max-w-3xl mx-auto">
              Manage members, ministries, donations, events, volunteers, and communication—all
              from one secure platform. Empower your church with modern tools that save time and
              strengthen your community.
            </p>

            <button
              className="px-5 py-[10px] mt-8 bg-secondary text-lg text-white font-medium rounded-[8px] hover:shadow-md hover:opacity-90"
              onClick={() => navigate("/free-trial")}
            >
              Get Started Free
            </button>
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-24">
        <div className="mx-auto max-w-7xl px-6">

          {/* Heading */}
          <div className="mx-auto max-w-3xl text-center">
            <span className="different_heading lg:text-[68px] text-[48px] font-semibold text-black leading-[1]">
              Built to Strengthen<br />
              <span className="different_heading italic text-secondary">
                Every Ministry
              </span>
            </span>

            <p className="mt-8 text-lg text-[#334155] leading-8 max-w-3xl mx-auto">
              Everything your church needs to manage members, ministries,
              communication, events, and giving—all in one secure, easy-to-use platform
              designed to help your community grow together.
            </p>
          </div>

          {/* Features */}
          <div className="mt-20 grid gap-y-8 gap-x-14 md:grid-cols-2 lg:grid-cols-3">

            {features.map((item) => {

              return (
                <div
                  key={item.title}
                  className="flex flex-col items-center text-center"
                >
                  <div
                    className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${item.bg}`}
                  >
                    <span className={`material-symbols-outlined text-[34px] ${item.color}`}>
                      {item.icon}
                    </span>
                  </div>

                  <h4 className="text-[18px] font-medium text-black">
                    {item.title}
                  </h4>

                  <p className="mt-2 max-w-[300px] text-[13px] leading-4 text-[#040404]">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="mobile-app" className="bg-[#e1f4fc] py-18 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">

          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left Content */}
            <div>
              <span className="different_heading lg:text-[68px] text-[48px] font-semibold text-black leading-[1]">
                One Platform For<br />
                <span className="different_heading italic text-secondary">
                  Every Ministry
                </span>
              </span>

              <p className="mt-8 text-lg text-[#334155] leading-8 max-w-3xl mx-auto">
                Simplify church management with powerful tools designed to help
                your ministry stay organized, connected, and engaged. Manage
                members, communicate effortlessly, and grow your church community
                from one centralized platform.
              </p>

              <div className="mt-10 flex flex-wrap gap-5">

                <a
                  href="https://apps.apple.com/us/app/my-church-directory-app/id6787551672"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/images/apple-store-badge.png"
                    alt="App Store"
                    className="h-16 w-auto hover:scale-105 duration-300"
                  />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.datarepo.directory&hl=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/images/google-play-badge.png"
                    alt="Google Play"
                    className="h-16 w-auto hover:scale-105 duration-300"
                  />
                </a>
              </div>

            </div>

            {/* Right Phones */}
            <div className="relative flex justify-center items-center h-[520px]">
              <img src="/images/app_mockups.png" className="w-full h-auto" alt="app-mockups" />
            </div>

          </div>

        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-5xl mx-auto px-6">

          {/* Heading */}

          <div className="text-center">
            <span className="different_heading lg:text-[68px] text-[48px] font-semibold text-black leading-[1]">
              Frequently Ask{" "}
              <span className="different_heading italic text-secondary">
                Questions
              </span>
            </span>
            <p className="mt-8 text-lg text-[#334155] leading-8 max-w-3xl mx-auto">
              Find answers to common questions about My Church Directory and how
              it can help your church thrive.
            </p>

          </div>

          {/* Accordion */}

          <div className="mt-16 space-y-5">

            {faqData.slice(0, 6)?.map((faq, index) => {
              const open = active === index;

              return (
                <div
                  key={index}
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setActive(open ? -1 : index)}
                    className="w-full flex items-center gap-5 px-8 py-4 text-left"
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full transition
                    ${open
                          ? "bg-secondary text-white"
                          : "bg-gray-100 text-gray-400"
                        }`}
                    >
                      {open ? (
                        <span className="material-symbols-outlined text-[24px]">
                          remove
                        </span>

                      ) : (
                        <span className="material-symbols-outlined text-[24px]">
                          add
                        </span>
                      )}
                    </div>

                    <h3 className="text-[18px] font-medium text-gray-800">
                      {faq.question}
                    </h3>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${open
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                      }`}
                  >
                    <div className="overflow-hidden">
                      <p className="pb-8 pl-[92px] pr-8 text-[14px] leading-4 text-[#585858]">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

          </div>

          <div className="flex items-center justify-center text-secondary mt-4" onClick={() => navigate("/faq")}>
            <span className="text-[14px] font-medium hover:underline hover:cursor-pointer">View All FAQs</span>
            <span className="material-symbols-outlined text-[24px]">
              arrow_right_alt
            </span>
          </div>
        </div>
      </section>
    </>
  )
}